import React, { useState, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSkills, useRegistryStats, useBulkDeleteSkills } from '../../hooks/useSkills';
import { SkillCard } from '../../components/skills/SkillCard';
import { SkillSearchBar } from '../../components/skills/SkillSearchBar';
import { ExportDialog } from '../../components/skills/ExportDialog';
import { SkillImportDialog } from '../../components/skills/SkillImportDialog';
import { SkillSearchParams } from '../../types/skill';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Spinner } from '../../components/common/Spinner';

// ── Stat chip ─────────────────────────────────────────────────────────────────
interface StatChipProps {
  label: string;
  value: number | string;
  accent?: string;
  onClick?: () => void;
  active?: boolean;
  darkMode: boolean;
}
const StatChip: React.FC<StatChipProps> = ({ label, value, accent, onClick, active, darkMode }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={[
      'flex flex-col items-center px-4 py-2.5 rounded-xl border transition-all',
      onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md' : 'cursor-default',
      active
        ? darkMode
          ? 'border-blue-500 bg-blue-900/30 shadow-[0_0_12px_rgba(59,130,246,0.3)]'
          : 'border-blue-400 bg-blue-50 shadow-md'
        : darkMode
          ? 'border-gray-700 bg-gray-800/60'
          : 'border-gray-200 bg-white',
    ].join(' ')}
  >
    <span
      className="text-2xl font-bold tabular-nums"
      style={{ color: accent ?? (darkMode ? '#e2e8f0' : '#1e293b') }}
    >
      {value}
    </span>
    <span className={`text-xs mt-0.5 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      {label}
    </span>
  </button>
);

// ── Active filter chip ────────────────────────────────────────────────────────
interface FilterChipProps {
  label: string;
  onRemove: () => void;
  darkMode: boolean;
}
const FilterChip: React.FC<FilterChipProps> = ({ label, onRemove, darkMode }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
    darkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-700' : 'bg-blue-50 text-blue-700 border border-blue-200'
  }`}>
    {label}
    <button onClick={onRemove} className="hover:opacity-70 ml-0.5">×</button>
  </span>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const SkillsHub: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const DEFAULT_PARAMS: Partial<SkillSearchParams> = { hide_code: true };
  const [searchParams, setSearchParams] = useState<Partial<SkillSearchParams>>(DEFAULT_PARAMS);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [selectedForExport, setSelectedForExport] = useState<string[]>([]);

  // Bulk selection state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const bulkDeleteMutation = useBulkDeleteSkills();

  // Fetch with current filters
  const { data, isLoading, error } = useSkills(searchParams);
  // Fetch unfiltered totals for stats
  const { data: allData } = useSkills({ limit: 1 });
  const { data: stats } = useRegistryStats();
  const skills = data?.skills ?? [];

  // ── Derived stats ──────────────────────────────────────────────────────────
  const totalAll = allData?.total ?? 0;
  const totalFiltered = data?.total ?? 0;
  const approvedCount: number = (stats?.by_tier?.approved ?? 0) + (stats?.by_tier?.auto_trusted ?? 0);
  const pendingCount: number = stats?.by_tier?.pending ?? 0;
  const codeCount: number = stats?.has_code_blocks ?? 0;

  // ── Active filter chips ────────────────────────────────────────────────────
  const activeFilters = useMemo(() => {
    const chips: { label: string; clear: () => void }[] = [];
    if (searchParams.q) chips.push({ label: `"${searchParams.q}"`, clear: () => setSearchParams(p => ({ ...p, q: undefined })) });
    if (searchParams.category) chips.push({ label: searchParams.category, clear: () => setSearchParams(p => ({ ...p, category: undefined })) });
    if (searchParams.tier) chips.push({ label: `tier: ${searchParams.tier}`, clear: () => setSearchParams(p => ({ ...p, tier: undefined })) });
    if (searchParams.hide_code) chips.push({ label: 'code hidden', clear: () => setSearchParams(p => ({ ...p, hide_code: false })) });
    return chips;
  }, [searchParams]);

  const handleSearch = (params: Partial<SkillSearchParams>) => {
    setSearchParams(prev => ({ ...prev, ...params }));
  };

  const clearAll = () => setSearchParams(DEFAULT_PARAMS);

  const toggleSelectMode = () => {
    setSelectMode(m => !m);
    setSelectedNames(new Set());
  };

  const toggleOne = (name: string) => {
    setSelectedNames(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const selectAll = () => setSelectedNames(new Set(skills.map(s => s.name)));
  const clearSelection = () => setSelectedNames(new Set());

  const handleBulkDelete = async () => {
    await bulkDeleteMutation.mutateAsync(Array.from(selectedNames));
    setSelectedNames(new Set());
    setSelectMode(false);
    setConfirmDelete(false);
  };

  return (
    <div className={`container mx-auto p-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>

      {/* ── Hero header ──────────────────────────────────────────────────── */}
      <div className={`rounded-2xl mb-6 overflow-hidden border ${
        darkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
      } shadow-lg`}>
        {/* Gradient accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        <div className="p-5">
          {/* Title row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
            <div>
              <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
                Skills
              </h1>
              <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Curated markdown skills for agent prompt injection
              </p>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              {user?.is_superuser && (
                <>
                  <Link
                    to="/skills/sources"
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    Sources
                  </Link>
                  <Link
                    to="/skills/pending"
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                      darkMode ? 'bg-amber-900/40 hover:bg-amber-800/40 text-amber-300 border border-amber-700' : 'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200'
                    }`}
                  >
                    Review Queue
                    {pendingCount > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                        darkMode ? 'bg-amber-700 text-amber-100' : 'bg-amber-500 text-white'
                      }`}>
                        {pendingCount}
                      </span>
                    )}
                  </Link>
                </>
              )}
              <button
                onClick={() => setShowExport(true)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                Export Pack
              </button>
              <button
                onClick={() => setShowImport(true)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 shadow-sm`}
              >
                + Submit Skill
              </button>
              {user?.is_superuser && (
                <button
                  onClick={toggleSelectMode}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectMode
                      ? darkMode ? 'bg-red-800 hover:bg-red-700 text-white' : 'bg-red-100 hover:bg-red-200 text-red-700'
                      : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  {selectMode ? 'Cancel' : 'Select'}
                </button>
              )}
            </div>
          </div>

          {/* ── Stat chips row ──────────────────────────────────────────── */}
          <div className="flex flex-wrap gap-3 mb-5">
            <StatChip
              label="Total"
              value={totalAll}
              accent={darkMode ? '#93c5fd' : '#2563eb'}
              darkMode={darkMode}
            />
            <StatChip
              label="Approved"
              value={approvedCount}
              accent={darkMode ? '#86efac' : '#16a34a'}
              onClick={() => setSearchParams(p => ({ ...p, tier: p.tier === 'approved' ? undefined : 'approved' }))}
              active={searchParams.tier === 'approved'}
              darkMode={darkMode}
            />
            <StatChip
              label="Pending"
              value={pendingCount}
              accent={darkMode ? '#fcd34d' : '#d97706'}
              onClick={() => setSearchParams(p => ({ ...p, tier: p.tier === 'pending' ? undefined : 'pending' }))}
              active={searchParams.tier === 'pending'}
              darkMode={darkMode}
            />
            {codeCount > 0 && (
              <StatChip
                label="Has Code"
                value={codeCount}
                accent={darkMode ? '#fb923c' : '#ea580c'}
                onClick={() => setSearchParams(p => ({ ...p, hide_code: p.hide_code === false ? true : false }))}
                active={searchParams.hide_code === false}
                darkMode={darkMode}
              />
            )}
            {/* Filtered count — only show when different from total */}
            {activeFilters.length > 0 && totalFiltered !== totalAll && (
              <StatChip
                label="Showing"
                value={totalFiltered}
                accent={darkMode ? '#c4b5fd' : '#7c3aed'}
                darkMode={darkMode}
              />
            )}
          </div>

          {/* ── Search bar ──────────────────────────────────────────────── */}
          <SkillSearchBar
            onSearch={handleSearch}
            initialParams={searchParams}
          />

          {/* ── Active filter chips ─────────────────────────────────────── */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Active:</span>
              {activeFilters.map((f) => (
                <FilterChip key={f.label} label={f.label} onRemove={f.clear} darkMode={darkMode} />
              ))}
              <button
                onClick={clearAll}
                className={`text-xs underline ${darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}
              >
                clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Bulk action bar ──────────────────────────────────────────────── */}
      {selectMode && (
        <div className={`flex items-center gap-3 mb-4 px-4 py-2.5 rounded-xl border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {selectedNames.size} selected
          </span>
          <button
            onClick={selectAll}
            className={`text-xs px-2.5 py-1 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
          >
            Select all ({skills.length})
          </button>
          <button
            onClick={clearSelection}
            className={`text-xs px-2.5 py-1 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
          >
            Clear
          </button>
          <div className="flex-1" />
          <button
            onClick={() => setConfirmDelete(true)}
            disabled={selectedNames.size === 0 || bulkDeleteMutation.isLoading}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors ${
              selectedNames.size === 0
                ? 'bg-red-300 cursor-not-allowed'
                : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            Delete {selectedNames.size > 0 ? `(${selectedNames.size})` : ''}
          </button>
        </div>
      )}

      {/* ── Grid ─────────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex justify-center p-12"><Spinner size="lg" /></div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">Failed to load skills</div>
      ) : skills.length === 0 ? (
        <div className={`text-center py-16 rounded-xl border border-dashed ${
          darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
        }`}>
          <p className="text-2xl mb-2">🔍</p>
          <p className="text-sm font-medium mb-1">No skills found</p>
          <p className="text-xs mb-4">
            {activeFilters.length > 0
              ? 'Try adjusting your filters'
              : 'Import skills from a source repo or submit your own'}
          </p>
          {activeFilters.length > 0 && (
            <button
              onClick={clearAll}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <p className={`text-xs mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {totalFiltered} skill{totalFiltered !== 1 ? 's' : ''}
            {activeFilters.length > 0 ? ' matching filters' : ''}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {skills.map((skill) => (
              <div key={skill.name} className="relative">
                {selectMode && (
                  <button
                    onClick={() => toggleOne(skill.name)}
                    className={`absolute top-2 right-2 z-10 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedNames.has(skill.name)
                        ? 'bg-red-500 border-red-500 text-white'
                        : darkMode ? 'border-gray-500 bg-gray-700' : 'border-gray-300 bg-white'
                    }`}
                  >
                    {selectedNames.has(skill.name) && <span className="text-xs leading-none">✓</span>}
                  </button>
                )}
                <SkillCard
                  skill={skill}
                  onClick={selectMode ? () => toggleOne(skill.name) : () => navigate(`/skills/${skill.name}`)}
                  onAttach={selectMode ? undefined : () => {
                    setSelectedForExport([skill.name]);
                    setShowExport(true);
                  }}
                />
                {selectMode && selectedNames.has(skill.name) && (
                  <div className={`absolute inset-0 rounded-lg border-2 border-red-500 pointer-events-none ${darkMode ? 'bg-red-900/10' : 'bg-red-50/40'}`} />
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Bulk delete confirm modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`w-full max-w-sm mx-4 rounded-lg shadow-2xl border p-6 ${
            darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}>
            <h3 className="text-lg font-semibold mb-2">Delete {selectedNames.size} skill{selectedNames.size !== 1 ? 's' : ''}?</h3>
            <p className={`text-sm mb-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              This cannot be undone. The skill files will be permanently removed from the registry.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isLoading}
                className="px-4 py-2 rounded-md text-sm text-white bg-red-500 hover:bg-red-600 disabled:opacity-50"
              >
                {bulkDeleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <SkillImportDialog isOpen={showImport} onClose={() => setShowImport(false)} />
      <ExportDialog
        isOpen={showExport}
        onClose={() => { setShowExport(false); setSelectedForExport([]); }}
        initialSkillNames={selectedForExport}
        availableSkills={skills}
      />
    </div>
  );
};

export default SkillsHub;
